// Per-route document head manager. Hand-rolled instead of react-helmet so the
// SPA carries no extra dependency; index.html holds the static defaults that
// non-JS scrapers (social link previews) see, and this overrides them for
// crawlers that render JS (Googlebot).
import { useEffect } from 'react';
import { canonicalOrigin, canonicalUrl } from '../lib/brand.js';

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

export default function Seo({
  title,
  description,
  noindex = false,
  jsonLd = null,
  image = null,
  imageAlt = null
}) {
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : null;

  useEffect(() => {
    if (title) document.title = title;
    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : null);
    // Mostly belt-and-braces: link scrapers don't run JS, so the served HTML's
    // og:image (index.html's default, or the prerender's per-route override) is
    // what actually gets unfurled. This keeps client-side navigation consistent
    // with that, and drops the stale width/height when a route swaps the image.
    if (image) {
      const abs = image.startsWith('http') ? image : canonicalOrigin() + image;
      upsertMeta('property', 'og:image', abs);
      upsertMeta('property', 'og:image:alt', imageAlt);
      upsertMeta('property', 'og:image:width', null);
      upsertMeta('property', 'og:image:height', null);
    }

    // Same URL the prerender bakes in, on every host: the brand's domain and
    // no trailing slash.
    const href = canonicalUrl();
    upsertMeta('property', 'og:url', href);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', href);

    let script = document.head.querySelector('script[data-seo-jsonld]');
    if (jsonLdText) {
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-jsonld', '');
        document.head.appendChild(script);
      }
      script.textContent = jsonLdText;
    } else {
      script?.remove();
    }
  }, [title, description, noindex, jsonLdText, image, imageAlt]);

  return null;
}
